// ============================================================================
// VersionAramis.js — Quelle version d'Aramis tourne dans CE navigateur ?
// ============================================================================
// Trois corrections livrées les 7 et 8 septembre 2026 ont été signalées comme
// « toujours pas résolues » alors qu'elles étaient en ligne : le navigateur
// exécutait une version antérieure, et rien à l'écran ne permettait de s'en
// apercevoir. D'où ce badge.
//
// La version affichée est celle du CACHE réellement servi (`caches.keys()`
// renvoie « aramis-v573 ») : c'est la vérité du navigateur, pas ce que le
// serveur a publié. Le clic force la mise à jour — désinscription du service
// worker, purge des caches, rechargement — c'est-à-dire le geste que « Cmd+R
// deux fois » ne fait pas toujours.
// ============================================================================
import { useEffect, useState } from "react";
import { IconeTexte } from "./icones";

const NOM_CACHE = /^aramis-v(\d+)/;

export default function VersionAramis({ visible = true }) {
  const [version, setVersion] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [survol, setSurvol] = useState(false);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const noms = await caches.keys();
        const v = noms.map((n) => NOM_CACHE.exec(n)).filter(Boolean)
          .map((m) => Number(m[1])).sort((a, b) => b - a)[0];
        if (!annule && v) setVersion(String(v));
      } catch { /* caches indisponible (navigation privée stricte, http) */ }
    })();
    return () => { annule = true; };
  }, []);

  const forcerMiseAJour = async () => {
    setEnCours(true);
    try {
      // 1. les caches du service worker, 2. le service worker lui-même.
      try {
        const noms = await caches.keys();
        await Promise.all(noms.map((n) => caches.delete(n)));
      } catch { /* rien à purger */ }
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch { /* pas de service worker */ }
    } finally {
      // `reload(true)` n'existe plus : on change l'URL pour contourner le
      // cache HTTP de l'index, sinon le navigateur resert la même page.
      const u = new URL(window.location.href);
      u.searchParams.set("maj", String(Date.now()).slice(-6));
      window.location.replace(u.toString());
    }
  };

  if (!visible || !version) return null;

  return (
    <button
      onClick={forcerMiseAJour}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      disabled={enCours}
      title="Version d'Aramis chargée dans ce navigateur. Cliquez pour forcer la mise à jour (purge du cache et rechargement)."
      style={{
        // Au-dessus de la barre d onglets au doigt ; à 10px du bas au bureau.
        position: "fixed", right: 10, bottom: "calc(10px + var(--h-onglets, 0px))", zIndex: 9998,
        border: "1px solid var(--border-color, #D6E0E7)", borderRadius: "var(--radius-xl)",
        background: "var(--bg-card, #fff)", color: "var(--text-sub, #64748B)",
        fontSize: "var(--fs-micro)", fontWeight: 700, padding: survol ? "6px 12px" : "4px 9px",
        cursor: enCours ? "wait" : "pointer", opacity: survol ? 1 : 0.55,
        boxShadow: survol ? "0 4px 14px rgba(0,0,0,.12)" : "none",
        transition: "opacity .15s, padding .15s",
        fontFamily: "Montserrat, Arial, sans-serif",
      }}>
      {enCours ? <><IconeTexte nom="hourglass"/> Mise à jour…</> : survol ? `v${version} · Mettre à jour` : `v${version}`}
    </button>
  );
}
